var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faChartSimple, faCodeCompare, faCoins, faLocationDot, faPhoneArrowDownLeft, faUserHeadset, faUserPlus, faUsers } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, CircularProgress, Typography, styled } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAPI } from '../../state/api';
import { useMultiGeoEnabled } from '../app/selectors';
import { Tab, Tabs } from '../common';
import { DashboardAssistant } from './assistant';
var DASHBOARDS_SSO_API = '/api/dashboards/sso';
/** Posted by the dashboards service when an embedded session idles out (cinareo_sso.py). */
var SESSION_ENDED = 'cinareo:session-ended';
/** Posted by the dashboards service with its path as the user moves around (superset_config.py). */
var LOCATION = 'cinareo:location';
/** Posted by the dashboards service with a dashboard's native filter values (superset_config.py). */
var FILTERS = 'cinareo:filters';
/** The curated dashboards, in tab order. Keys match the api's DASHBOARD_VIEWS. */
var CURATED_VIEWS = {
  overview: faChartSimple,
  demand: faPhoneArrowDownLeft,
  staffing: faUsers,
  hiring: faUserPlus,
  service: faUserHeadset,
  costs: faCoins,
  locations: faLocationDot,
  scenarios: faCodeCompare
};
/** The dashboard list, where users open their own dashboards and build new ones. */
var LIBRARY = 'library';
var Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});
var TabIcon = styled(FontAwesomeIcon)(function (_a) {
  var theme = _a.theme;
  return {
    color: theme.palette.primary.main
  };
});
var Frame = styled('iframe')({
  border: 'none',
  width: '100%',
  height: '100%'
});
var Cover = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(4),
    backgroundColor: theme.palette.background.default
  };
});
/** The handoff url, carrying filter values to the view (see packages/superset/cinareo_sso.py). */
var withFilters = function (handoffUrl, filters) {
  var url = new URL(handoffUrl);
  if (filters) {
    url.searchParams.set('filters', filters);
  }
  return url.toString();
};
/**
 * Dashboards, embedded from the dashboards service. Each view opens through a one-time SSO
 * handoff url minted by the api for the current user (see packages/superset/cinareo_sso.py);
 * the service scopes every query to the user's org.
 */
export var Dashboards = function () {
  var _a;
  var t = useTranslation().t;
  var multiGeoEnabled = useMultiGeoEnabled();
  var curated = Object.keys(CURATED_VIEWS).filter(function (view) {
    return view !== 'locations' || multiGeoEnabled;
  });
  var _b = useSearchParams(),
    searchParams = _b[0],
    setSearchParams = _b[1];
  var requested = searchParams.get('view');
  var view = requested === LIBRARY ? LIBRARY : (_a = curated.find(function (v) {
    return v === requested;
  })) !== null && _a !== void 0 ? _a : 'overview';
  var _c = useAPI(DASHBOARDS_SSO_API),
    _d = _c[0],
    data = _d.data,
    requesting = _d.requesting,
    error = _d.error,
    fetchSso = _c[1],
    clearSso = _c[2];
  var _e = useState(),
    loadedUrl = _e[0],
    setLoadedUrl = _e[1];
  /** A page of the dashboards opened on the handoff's session, such as a chart the assistant made. */
  var _f = useState(),
    openedUrl = _f[0],
    setOpenedUrl = _f[1];
  /** Where the frame is, as it last reported. */
  var _g = useState(),
    framePath = _g[0],
    setFramePath = _g[1];
  var frameRef = useRef(null);
  /** The latest value of every dashboard filter seen on any tab, by filter id. */
  var filtersRef = useRef({});
  /** The filter values the current handoff carries, fixed when it is requested. */
  var handoffFiltersRef = useRef();
  /** Opens the view through a fresh handoff, carrying the filters chosen on the other tabs. */
  var openView = useCallback(function () {
    handoffFiltersRef.current = view === LIBRARY ? undefined : JSON.stringify(filtersRef.current);
    fetchSso(undefined, {
      view: view
    });
  }, [view, fetchSso]);
  useEffect(function () {
    setOpenedUrl(undefined);
    setFramePath(undefined);
    openView();
    // A handoff url works once, so a remount or another view must never reuse it.
    return clearSso;
  }, [openView, clearSso]);
  var handoffUrl = requesting || !(data === null || data === void 0 ? void 0 : data.url) ? undefined : withFilters(data.url, handoffFiltersRef.current);
  /** The dashboards service's origin: the frame only ever shows pages from it. */
  var origin = handoffUrl && new URL(handoffUrl).origin;
  var url = handoffUrl && (openedUrl !== null && openedUrl !== void 0 ? openedUrl : handoffUrl);
  var loaded = url !== undefined && url === loadedUrl;
  var select = function (next) {
    return setSearchParams({
      view: next
    });
  };
  var openInFrame = function (path) {
    var target = handoffUrl && new URL(path, handoffUrl);
    if (target && target.origin === origin) {
      setOpenedUrl(target.toString());
    }
  };
  useEffect(function () {
    // A session that ends after the view loaded has idled out: reopen the view with a fresh
    // handoff. One that ends before could not start, and reopening would only repeat that.
    var handleMessage = function (event) {
      var _a, _b, _c;
      if (event.origin !== origin || event.source !== ((_a = frameRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow)) {
        return;
      }
      if (loaded && event.data === SESSION_ENDED) {
        setOpenedUrl(undefined);
        openView();
      } else if (((_b = event.data) === null || _b === void 0 ? void 0 : _b.type) === LOCATION && typeof event.data.path === 'string') {
        setFramePath(event.data.path);
      } else if (((_c = event.data) === null || _c === void 0 ? void 0 : _c.type) === FILTERS && typeof event.data.filters === 'object') {
        filtersRef.current = __assign(__assign({}, filtersRef.current), event.data.filters);
      }
    };
    window.addEventListener('message', handleMessage);
    return function () {
      return window.removeEventListener('message', handleMessage);
    };
  }, [origin, loaded, openView]);
  return _jsxs(Box, __assign({
    sx: {
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 140px)'
    }
  }, {
    children: [_jsx(Box, __assign({
      sx: {
        bgcolor: 'common.white',
        padding: '1rem 1.25rem 0'
      }
    }, {
      children: _jsxs(Header, {
        children: [_jsx(Tabs, __assign({
          value: view === LIBRARY ? false : view,
          variant: "scrollable",
          scrollButtons: "auto"
        }, {
          children: curated.map(function (key) {
            return _jsx(Tab, {
              value: key,
              icon: _jsx(TabIcon, {
                icon: CURATED_VIEWS[key],
                size: "lg"
              }),
              iconPosition: "start",
              label: _jsxs(Typography, __assign({
                variant: "subtitle2"
              }, {
                children: ["\u00A0", t("dashboards.views.".concat(key))]
              })),
              onClick: function () {
                return select(key);
              }
            }, key);
          })
        })), _jsx(Button, __assign({
          variant: view === LIBRARY ? 'contained' : 'outlined',
          onClick: function () {
            return select(LIBRARY);
          },
          sx: {
            marginBottom: 2
          }
        }, {
          children: t('dashboards.views.library')
        }))]
      })
    })), _jsxs(Box, __assign({
      sx: {
        position: 'relative',
        flex: 1,
        minHeight: 0
      }
    }, {
      children: [url && _jsx(Frame, {
        ref: frameRef,
        src: url,
        title: t("dashboards.views.".concat(view)),
        onLoad: function () {
          return setLoadedUrl(url);
        }
      }, url), error ? _jsxs(Cover, {
        children: [_jsx(Typography, {
          children: t('dashboards.unavailable')
        }), _jsx(Button, __assign({
          variant: "outlined",
          onClick: openView
        }, {
          children: t('dashboards.retry')
        }))]
      }) : !loaded && _jsx(Cover, {
        children: _jsx(CircularProgress, {})
      }), _jsx(DashboardAssistant, {
        view: view,
        path: framePath,
        onOpen: openInFrame
      })]
    }))]
  }));
};